'use server'

import type { ServiceLabels } from '@/lib/form-state/util'

/** Rótulos e opções dos gatilhos, condições e campos dos fluxos de vendas (docs/sales-flows.md). */

export type AutomationTriggerEvent =
    | 'CHAT_STARTED' | 'LEAD_CREATED' | 'LEAD_UPDATED' | 'LEAD_ASSIGNED' | 'LEAD_SCORE_CHANGED'
    | 'LEAD_SENTIMENT_CHANGED' | 'DEAL_SENTIMENT_CHANGED' | 'LEAD_CONVERTED' | 'DEAL_CREATED' | 'DEAL_STAGE_CHANGED'
    | 'DEAL_CLOSED' | 'DEAL_VALUE_CHANGED' | 'SLA_BREACHED' | 'FOLLOW_UP_COMPLETED'
    | 'BILLING_CREATED' | 'BILLING_PAID' | 'BILLING_ERROR'
    | 'APPOINTMENT_CONFIRMED' | 'APPOINTMENT_PARTNER_CREATED'
    | 'CONSULTATION_CREATED' | 'EXAM_CREATED' | 'PROCEDURE_CREATED' | 'TELECONSULTATION_CREATED'
    | 'APPOINTMENT_MANUAL_CREATED' | 'APPOINTMENT_MANUAL_PAID' | 'APPOINTMENT_MANUAL_BILLING_PENDING'

export type ConditionOperator = 'EQUALS' | 'NOT_EQUALS' | 'GREATER_THAN' | 'LESS_THAN' | 'IN' | 'NOT_IN' | 'EXISTS' | 'NOT_EXISTS' | 'CONTEXT_MATCH'

export const TRIGGER_EVENT_LABELS: Record<AutomationTriggerEvent, string> = {
    CHAT_STARTED: 'Atendimento iniciado',
    LEAD_CREATED: 'Lead criado',
    LEAD_UPDATED: 'Lead atualizado',
    LEAD_ASSIGNED: 'Lead atribuído',
    LEAD_SCORE_CHANGED: 'Score do lead alterado',
    LEAD_SENTIMENT_CHANGED: 'Sentimento do lead alterado',
    DEAL_SENTIMENT_CHANGED: 'Sentimento da oportunidade alterado',
    LEAD_CONVERTED: 'Lead convertido',
    DEAL_CREATED: 'Oportunidade criada',
    DEAL_STAGE_CHANGED: 'Oportunidade mudou de etapa',
    DEAL_CLOSED: 'Oportunidade fechada',
    DEAL_VALUE_CHANGED: 'Valor da oportunidade alterado',
    SLA_BREACHED: 'SLA violado',
    FOLLOW_UP_COMPLETED: 'Follow-up concluído',
    BILLING_CREATED: 'Cobrança criada',
    BILLING_PAID: 'Cobrança paga',
    BILLING_ERROR: 'Erro na cobrança',
    APPOINTMENT_CONFIRMED: 'Agendamento confirmado',
    APPOINTMENT_PARTNER_CREATED: 'Agendamento em parceiro',
    CONSULTATION_CREATED: 'Consulta criada',
    EXAM_CREATED: 'Exame criado',
    PROCEDURE_CREATED: 'Procedimento criado',
    TELECONSULTATION_CREATED: 'Teleconsulta criada',
    APPOINTMENT_MANUAL_CREATED: 'Agendamento Manual Criado',
    APPOINTMENT_MANUAL_PAID: 'Agendamento Manual Pago',
    APPOINTMENT_MANUAL_BILLING_PENDING: 'Agendamento Manual com Cobrança Pendente',
}

/**
 * `TRIGGER_EVENT_LABELS` no vocabulário do segmento: fora da saúde,
 * Procedimento vira Pedido e Consulta vira Avaliação.
 */
export function getTriggerEventLabels(
    labels: ServiceLabels
): Record<AutomationTriggerEvent, string> {
    return {
        ...TRIGGER_EVENT_LABELS,
        CONSULTATION_CREATED: `${labels.Consultation} criada`,
        EXAM_CREATED: `${labels.Exam} criado`,
        PROCEDURE_CREATED: `${labels.Order} criado`,
    }
}

/**
 * Campos permitidos no passo "Alterar campo" (SET_FIELD). ESPELHA a whitelist do
 * backend (`automation-field-whitelist.ts`) — mudou lá, mude aqui. `hint` orienta o
 * formato do valor (o backend coage para o tipo certo).
 */
export const UPDATE_LEAD_FIELD_OPTIONS: {
    value: string
    label: string
    hint?: string
}[] = [
    { value: 'name', label: 'Nome' },
    { value: 'email', label: 'E-mail' },
    { value: 'phoneNumber', label: 'Telefone' },
    { value: 'status', label: 'Status' },
    { value: 'source', label: 'Origem' },
    { value: 'notes', label: 'Observações' },
    { value: 'position', label: 'Cargo' },
    { value: 'temperature', label: 'Temperatura (COLD/WARM/HOT)' },
    { value: 'lifecycleStatus', label: 'Ciclo de vida' },
]

export const UPDATE_DEAL_FIELD_OPTIONS: {
    value: string
    label: string
    hint?: string
}[] = [
    { value: 'title', label: 'Título' },
    { value: 'totalValue', label: 'Valor (em centavos)', hint: 'número' },
    { value: 'probability', label: 'Probabilidade (%)', hint: 'número' },
    { value: 'status', label: 'Status' },
    { value: 'category', label: 'Categoria' },
    {
        value: 'expectedCloseDate',
        label: 'Data prevista de fechamento',
        hint: 'AAAA-MM-DD',
    },
]

export const OPERATOR_LABELS: Record<ConditionOperator, string> = {
    EQUALS: 'Igual a',
    NOT_EQUALS: 'Diferente de',
    GREATER_THAN: 'Maior que',
    LESS_THAN: 'Menor que',
    IN: 'Contido em',
    NOT_IN: 'Não contido em',
    EXISTS: 'Existe',
    NOT_EXISTS: 'Não existe',
    CONTEXT_MATCH: 'Contexto da conversa (IA)',
}

export const CONDITION_FIELD_OPTIONS = [
    { value: 'lead.totalScore', label: 'Score do Lead' },
    { value: 'deal.currentSentiment', label: 'Sentimento da Oportunidade' },
    { value: 'lead.temperature', label: 'Temperatura do Lead' },
    { value: 'lead.lifecycleStatus', label: 'Ciclo de Vida do Lead' },
    { value: 'lead.source', label: 'Origem do Lead' },
    { value: 'lead.hasBudget', label: 'Lead possui orçamento' },
    { value: 'lead.isDecisionMaker', label: 'Lead é tomador de decisão' },
    { value: 'lead.hasNeed', label: 'Lead possui necessidade' },
    { value: 'lead.assignedUserId', label: 'Lead atribuído (ID)' },
    { value: 'lead.tags', label: 'Tags do Lead' },
    { value: 'deal.status', label: 'Status da Oportunidade' },
    { value: 'deal.totalValue', label: 'Valor da Oportunidade' },
    { value: 'deal.stageId', label: 'Etapa da Oportunidade' },
    { value: 'trigger.oldStageId', label: 'Etapa anterior (trigger)' },
    { value: 'trigger.newStageId', label: 'Nova etapa (trigger)' },
    { value: 'trigger.oldSentiment', label: 'Sentimento anterior (trigger)' },
    { value: 'trigger.newSentiment', label: 'Novo sentimento (trigger)' },
    { value: 'conversation.context', label: 'Contexto da conversa (IA)' },
]
